import type { UpdatePreferencesRequest } from '@gestao/shared'
import { toast } from '@gestao/ui'
import { useMutation } from '@tanstack/react-query'
import { useEffect } from 'react'

import * as t from '../../i18n/pt-BR'
import { session, useSessionStore } from '../../lib/session'
import { authApi } from '../auth/api'
import { applyPreferences, readStoredPreferences } from './theme'

/** Aplica no `<html>` a preferência do usuário logado (ou a última guardada, sem sessão). */
export function usePreferencesSync(): void {
  const theme = useSessionStore((state) => state.user?.preferences.theme)
  const density = useSessionStore((state) => state.user?.preferences.density)

  useEffect(() => {
    applyPreferences(theme && density ? { theme, density } : readStoredPreferences())
  }, [theme, density])
}

/**
 * Salva tema e densidade na API com atualização otimista (a tela muda na hora). As mudanças
 * vão em fila (`scope`), então a resposta mais recente sempre é a última aplicada.
 */
export function useUpdatePreferences() {
  return useMutation({
    scope: { id: 'preferences' },
    mutationFn: (patch: UpdatePreferencesRequest) => authApi.updatePreferences(patch),
    onMutate: (patch) => {
      const user = session.get().user
      if (!user) return { previous: null }
      const previous = user.preferences
      session.setUser({ ...user, preferences: { ...previous, ...patch } })
      return { previous }
    },
    onSuccess: (user) => session.setUser(user),
    onError: (_error, _patch, context) => {
      const user = session.get().user
      if (user && context?.previous) session.setUser({ ...user, preferences: context.previous })
      toast.error(t.shell.preferenceError)
    },
  })
}
